import { useDispatch, useSelector } from "react-redux";
import { resetForm, resetUpdatePassword, setAccountSettingMode, setUpdateMode, toggleUpdatePasswordVisibility, updateField } from "../../features/it-user-forms/user-forms-slice";
import { useMemo, useState } from "react";
import { suffix } from "../../data/system";
import { getErrorMessage } from "../../utils/get-error-message.utils";
import { useQueries } from "../services/queries/use-queries";
import { ROLE_IDs } from "../../constant/it-constant";
import { clearConfirmTypeModal, clearTypeModal, clearTypeOfStatus, clearUserId, setConfirmTypeModal, setTyeOfStatus, setTypeModal, setUserId } from "../../features/gms-state-and-modal/modal-slice";
import { selectUserId } from "../../features/auth/auth-slice";
import { UserStatus } from "../../types/type";
import { CreateAccountForm } from "../../api/it/account/account-api-request";
import { PasswordFormState } from "../../features/it-user-forms/user-form-state";
import { UserInfo } from "../../api/user/user-api-state";
import { useToastify } from "../../library/toastify/toast-provider";
import { useCreateNewUserMutation, useUpdateUserAccountInfoMutation } from "../../api/it/account/account-api";
import { useUpdateProfileMutation } from "../../api/user/user-api";
import { useUserManagementDataState } from "../data-state/use-user-management-data-state";

type ConfirmAction = 'update' | 'resetPassword' | 'activate' | 'deactivate' | null;
export interface CategoryLabel {
    name: string;
    id: number;
}

export const useCreateUser = () => {

    const { showToastNotify } = useToastify();
    const { department, isFetchingDepartment, location, isFetchingLocation: isFetchingLocationType, role, isFetchingRole: isFetchingRoleType, reportingHead, isFetchingReportingHead: isFetchingReportingHeadType } = useQueries()
    const [createAccount, { isLoading: isLoadingCreateAccount }] = useCreateNewUserMutation();
    const [updateUserAccountInfo, { isLoading: isLoadingUpdateUserAccountInfo, isError: isErrorUpdateUserAccountInfo }] = useUpdateUserAccountInfoMutation();
    const [updateProfile, { isLoading: isLoadingUpdateProfile }] = useUpdateProfileMutation();
    const { roleId, departmentId } = useUserManagementDataState();

    const activeDepartments = useMemo(
        () => department?.filter(item => item.is_active) ?? [],
        [department]
    );

    const activeLocations = useMemo(
        () => location?.filter(item => item.is_active) ?? [],
        [location]
    );

    const activeRoles = useMemo(
        () => role?.filter(item => item.is_active) ?? [],
        [role]
    );

    const reportingHeadFormat = useMemo(() => {
        return reportingHead
            ?.filter((item) => Number(item.department_id) === Number(departmentId))
            .map((item) => ({
                id: item.user_id,
                name: item.full_name,
                subTitle: item.department,
            })) || [];
    }, [reportingHead, departmentId]);

    const dispatch = useDispatch();
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);

    const defaultOption = useMemo(
        () => ({ id: 0, name: "Select" }), []
    );

    const defaultOptionSub = useMemo(
        () => ({ id: 0, name: "Select", subTitle: "" }), []
    );

    const defaultOptionSuffix = useMemo(
        () => ({ id: 0, name: "None" }), []
    );

    const suffixOptions = useMemo(
        () => [
            defaultOptionSuffix,
            ...(suffix?.map(item => ({
                id: item.id,
                name: item.name,
            })) ?? []),
        ],
        [defaultOptionSuffix]
    );

    const roleOptions = useMemo(
        () => [
            defaultOption,
            ...(activeRoles?.map(item => ({
                id: item.id,
                name: item.name,
            })) ?? []),
        ],
        [activeRoles, defaultOption]
    );
    // console.log("roleOptions: ", roleOptions)
    const departmentOptions = useMemo(
        () => [
            defaultOption,
            ...(activeDepartments?.map(item => ({
                id: item.id,
                name: item.name,
            })) ?? []),
        ],
        [activeDepartments, defaultOption]
    );
    // console.log("activeDepartments: ", activeDepartments)
    // console.log("departmentOptions: ", departmentOptions)
    const locationOptions = useMemo(
        () => [
            defaultOption,
            ...(activeLocations?.map(item => ({
                id: item.id,
                name: item.name,
            })) ?? []),
        ],
        [activeLocations, defaultOption]
    );

    const reportingHeadOptions = useMemo(
        () => [
            defaultOptionSub,
            ...(reportingHeadFormat?.map(item => ({
                id: item.id,
                name: item.name,
                subTitle: item.subTitle,
            })) ?? []),
        ],
        [reportingHeadFormat, defaultOptionSub]
    );
    // console.log("reportingHeadOptions: ", reportingHeadOptions)
    // console.log("reportingHead: ", reportingHead)
    // console.log("reportingHeadFormat: ", reportingHeadFormat)
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
    const [searchTypeSuffix, setSearchTypeSuffix] = useState<CategoryLabel | null>(defaultOptionSuffix);
    const [searchTypeRole, setSearchTypeRole] = useState<CategoryLabel | null>(defaultOption);
    const [searchTypeDepartment, setSearchTypeDepartment] = useState<CategoryLabel | null>(defaultOption);
    const [searchTypeLocation, setSearchTypeLocation] = useState<CategoryLabel | null>(defaultOption);
    const [searchReportingHead, setSearchReportingHead] = useState<CategoryLabel | null>(defaultOption);
    const [updatePosition, setUpdatePosition] = useState<string | null>(null);
    const {updateAccountId: selectIdUpdate, updatePassword, createAccountForms: selectCreateAccount} = useUserManagementDataState()
    const currentId = useSelector(selectUserId);

    const handleCreateAccountClick = () => {
        if (!selectCreateAccount.firstname.trim()) {
            showToastNotify("First Name is required.", 'warning');
            return
        }
        if (!selectCreateAccount.lastname.trim()) {
            showToastNotify("Last Name is required.", 'warning');
            return
        }
        // if (!selectCreateAccount.middlename.trim()) {
        //     showToastNotify("Middle Name is required.", 'warning');
        //     return
        // }
        if (!selectCreateAccount.email.trim()) {
            showToastNotify("Email is required.", 'warning');
            return
        }
        if (!selectCreateAccount.agent_code.trim()) {
            showToastNotify("Agent Code is required.", 'warning');
            return
        }
        if (!selectCreateAccount.phoneNumber || selectCreateAccount.phoneNumber.length !== 10 || !selectCreateAccount.phoneNumber.startsWith("9")) {
            showToastNotify("Phone Number is required.", 'warning');
            return
        }
        if (!selectCreateAccount.role || selectCreateAccount.role.id === 0 || selectCreateAccount.role.name === 'Select') {
            showToastNotify("Role is required.", 'warning');
            return
        }
        if (!selectCreateAccount.department || selectCreateAccount.department.id === 0 || selectCreateAccount.department.name === 'Select') {
            showToastNotify("Department is required.", 'warning');
            return
        }
        if (!selectCreateAccount.location || selectCreateAccount.location.id === 0 || selectCreateAccount.location.name === 'Select') {
            showToastNotify("Location is required.", 'warning');
            return
        }
        if(selectCreateAccount.role.id === ROLE_IDs.corporateFinancialExecutive && (!selectCreateAccount.reporting_to_id || selectCreateAccount.reporting_to_id.id === 0 || selectCreateAccount.reporting_to_id.name === 'Select')) {
            showToastNotify("Reporting head for CFE is required.", 'warning');
            return
        }
        setShowConfirmModal(true);
    };

    const handleSubmit = async () => {
        // e.preventDefault();

        if (!selectCreateAccount.firstname.trim()) {
            showToastNotify("First Name is required.", 'warning');
            return
        }
        if (!selectCreateAccount.lastname.trim()) {
            showToastNotify("Last Name is required.", 'warning');
            return
        }
        // if (!selectCreateAccount.middlename.trim()) {
        //     showToastNotify("Middle Name is required.", 'warning');
        //     return
        // }
        if (!selectCreateAccount.email.trim()) {
            showToastNotify("Email is required.", 'warning');
            return
        }
        if (!selectCreateAccount.agent_code.trim()) {
            showToastNotify("Agent Code is required.", 'warning');
            return
        }
        if (!selectCreateAccount.phoneNumber || selectCreateAccount.phoneNumber.length !== 10 || !selectCreateAccount.phoneNumber.startsWith("9")) {
            showToastNotify("Phone Number is required.", 'warning');
            return
        }
        if (!selectCreateAccount.role || selectCreateAccount.role.id === 0 || selectCreateAccount.role.name === 'Select') {
            showToastNotify("Role is required.", 'warning');
            return
        }
        if (!selectCreateAccount.department || selectCreateAccount.department.id === 0 || selectCreateAccount.department.name === 'Select') {
            showToastNotify("Department is required.", 'warning');
            return
        }
        if (!selectCreateAccount.location || selectCreateAccount.location.id === 0 || selectCreateAccount.location.name === 'Select') {
            showToastNotify("Location is required.", 'warning');
            return
        }
        if(selectCreateAccount.role.id === ROLE_IDs.corporateFinancialExecutive && (!selectCreateAccount.reporting_to_id || selectCreateAccount.reporting_to_id.id === 0 || selectCreateAccount.reporting_to_id.name === 'Select')) {
            showToastNotify("Reporting head for CFE is required.", 'warning');
            return
        }

        // if(selectCreateAccount.role.id === ROLE_IDs.teamLeader && (!selectCreateAccount.reporting_to_id || selectCreateAccount.reporting_to_id.id === 0 || selectCreateAccount.reporting_to_id.name === 'Select')) {
        //     showToastNotify("Reporting head for team lead is required.", 'warning');
        //     return
        // }

        const requiredReportingHeadGMSDepartment = (
            Number(selectCreateAccount.role.id) === ROLE_IDs.corporateFinancialExecutive
            // ||Number(selectCreateAccount.role.id) === ROLE_IDs.teamLeader
        );

        const isRoleIsSuperAdmin = Number(selectCreateAccount.role.id) === ROLE_IDs.systemAdmin;
        const isMarketingDepartment = selectCreateAccount.department.name === "Marketing Officer, GMS Support & Training";
        const isReportingHeadRequired = !isRoleIsSuperAdmin && requiredReportingHeadGMSDepartment && isMarketingDepartment;

        if (isReportingHeadRequired && (
            !selectCreateAccount.reporting_to_id ||
            !selectCreateAccount.reporting_to_id.id ||
            selectCreateAccount.reporting_to_id.name === "Select"
        )) {
            showToastNotify("Reporting head is required for this role and department.", "warning");
            return;
        }

        // Validate reporting head for ALL roles if selected
        if (
            selectCreateAccount.reporting_to_id?.id !== 0 &&
            selectCreateAccount.reporting_to_id?.id !== null &&
            selectCreateAccount.reporting_to_id?.id !== undefined
        ) {
            const selectedReportingHead = reportingHead?.find((item) => Number(item.user_id) === Number(selectCreateAccount.reporting_to_id?.id));

            // if (!selectedReportingHead) {
            //     showToastNotify("Invalid reporting head selected.", "warning");
            //     return;
            // }

            if (Number(selectedReportingHead?.department_id) !== Number(selectCreateAccount.department.id)) {
                showToastNotify(
                    `Reporting head must belong to ${selectCreateAccount.department.name}. Selected reporting head belongs to ${selectedReportingHead?.department}.`,
                    "warning"
                );
                return;
            }
        }

        const suffixName = selectCreateAccount.suffix?.name?.trim();
        const newAccountBody: CreateAccountForm = {
            firstname: selectCreateAccount.firstname,
            middlename: selectCreateAccount.middlename ?? "",
            lastname: selectCreateAccount.lastname,
            ...(suffixName && !["none", "select"].includes(suffixName.toLowerCase()) ? { suffix: suffixName } : {}),
            email: selectCreateAccount.email,
            agent_code: selectCreateAccount.agent_code,
            phoneNumber: `+63${selectCreateAccount.phoneNumber}`,
            role_id: selectCreateAccount.role?.id ?? null,
            location_id: selectCreateAccount.location?.id ?? null,
            department_id: selectCreateAccount.department?.id ?? null,
            reporting_to_id: !selectCreateAccount.reporting_to_id?.id ? null : selectCreateAccount.reporting_to_id.id,
            position: selectCreateAccount.position ?? null,
        };
        // console.log("newAccountBody: ", newAccountBody)
        try {
            const response = await createAccount(newAccountBody).unwrap()
            const messageResponse = response.message;
            showToastNotify(messageResponse, 'success');
            dispatch(resetForm())
            setSearchTypeSuffix(defaultOptionSuffix)
            setSearchTypeRole(defaultOption)
            setSearchTypeDepartment(defaultOption)
            setSearchTypeLocation(defaultOption)
            setShowConfirmModal(false);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, "error");
        }
    }

    const handleClear = () => {
        dispatch(resetForm());
        setSearchTypeSuffix(defaultOptionSuffix);
        setSearchTypeRole(defaultOption);
        setSearchTypeDepartment(defaultOption);
        setSearchTypeLocation(defaultOption);
    }

    const handleUpdateUser = async () => {
        if(!selectIdUpdate) {
            showToastNotify('User ID is missing.', 'warning');
            return;
        }

        if (!selectCreateAccount.firstname.trim()) {
            showToastNotify("First Name is required.", 'warning');
            return
        }
        if (!selectCreateAccount.lastname.trim()) {
            showToastNotify("Last Name is required.", 'warning');
            return
        }
        // if (!selectCreateAccount.middlename.trim()) {
        //     showToastNotify("Middle Name is required.", 'warning');
        //     return
        // }
        if (!selectCreateAccount.email.trim()) {
            showToastNotify("Email is required.", 'warning');
            return
        }
        if (!selectCreateAccount.agent_code.trim()) {
            showToastNotify("Agent Code is required.", 'warning');
            return
        }
        if (!selectCreateAccount.phoneNumber || selectCreateAccount.phoneNumber.length !== 10 || !selectCreateAccount.phoneNumber.startsWith("9")) {
            showToastNotify("Phone Number is required.", 'warning');
            return
        }
        if (!selectCreateAccount.role || selectCreateAccount.role.id === 0 || selectCreateAccount.role.name === 'Select') {
            showToastNotify("Role is required.", 'warning');
            return
        }
        if (!selectCreateAccount.department || selectCreateAccount.department.id === 0 || selectCreateAccount.department.name === 'Select') {
            showToastNotify("Department is required.", 'warning');
            return
        }
        if (!selectCreateAccount.location || selectCreateAccount.location.id === 0 || selectCreateAccount.location.name === 'Select') {
            showToastNotify("Location is required.", 'warning');
            return
        }
        if(selectCreateAccount.role.id === ROLE_IDs.corporateFinancialExecutive && (!selectCreateAccount.reporting_to_id || selectCreateAccount.reporting_to_id.id === 0 || selectCreateAccount.reporting_to_id.name === 'Select')) {
            showToastNotify("Reporting head for CFE is required.", 'warning');
            return
        }

        // if(selectCreateAccount.role.id === ROLE_IDs.teamLeader && (!selectCreateAccount.reporting_to_id || selectCreateAccount.reporting_to_id.id === 0 || selectCreateAccount.reporting_to_id.name === 'Select')) {
        //     showToastNotify("Reporting head for team lead is required.", 'warning');
        //     return
        // }

        const requiredReportingHeadGMSDepartment = (
            Number(selectCreateAccount.role.id) === ROLE_IDs.corporateFinancialExecutive
            // || Number(selectCreateAccount.role.id) === ROLE_IDs.teamLeader
        );

        const isRoleIsSuperAdmin = Number(selectCreateAccount.role.id) === ROLE_IDs.systemAdmin;
        const isMarketingDepartment = selectCreateAccount.department.name === "Marketing Officer, GMS Support & Training";
        const isReportingHeadRequired = !isRoleIsSuperAdmin && requiredReportingHeadGMSDepartment && isMarketingDepartment;

        if (isReportingHeadRequired && (
            !selectCreateAccount.reporting_to_id ||
            !selectCreateAccount.reporting_to_id.id ||
            selectCreateAccount.reporting_to_id.name === "Select"
        )) {
            showToastNotify("Reporting head is required for this role and department.", "warning");
            return;
        }

        // Validate reporting head for ALL roles if selected
        if (
            selectCreateAccount.reporting_to_id?.id !== 0 &&
            selectCreateAccount.reporting_to_id?.id !== null &&
            selectCreateAccount.reporting_to_id?.id !== undefined
        ) {
            const selectedReportingHead = reportingHead?.find((item) => Number(item.user_id) === Number(selectCreateAccount.reporting_to_id?.id));

            // if (!selectedReportingHead) {
            //     showToastNotify("Selected reporting head does not exist or is currently inactive.", "warning");
            //     return;
            // }

            // Validate if reporting head is active
            if (!selectedReportingHead?.userStatus) {
                showToastNotify(
                    `${selectedReportingHead?.full_name} is currently inactive. Please select an active reporting head.`,
                    "warning"
                );
                return;
            }

            // Validate department
            if (Number(selectedReportingHead?.department_id) !== Number(selectCreateAccount.department.id)) {
                showToastNotify(
                    `Reporting head must belong to ${selectCreateAccount.department.name}. Selected reporting head belongs to ${selectedReportingHead?.department}.`,
                    "warning"
                );
                return;
            }
        }

        try {
            const suffixName = selectCreateAccount.suffix?.name?.trim();
            const body = {
                firstname: selectCreateAccount.firstname,
                middlename: selectCreateAccount.middlename ?? "", 
                lastname: selectCreateAccount.lastname,
                ...(suffixName && !["none", "select"].includes(suffixName.toLowerCase()) ? { suffix: suffixName } : { suffix: null }),
                email: selectCreateAccount.email,
                agent_code: selectCreateAccount.agent_code,
                phoneNumber: `+63${selectCreateAccount.phoneNumber}`,
                role_id: selectCreateAccount.role.id,
                location_id: selectCreateAccount.location.id,
                department_id: selectCreateAccount.department.id,
                reporting_to_id: !selectCreateAccount.reporting_to_id?.id ? null : selectCreateAccount.reporting_to_id.id,
                position: selectCreateAccount.position ?? '',
            }
            // console.log("bodyUpdateUser: ", body)
            const response = await updateUserAccountInfo({userId: selectIdUpdate, body}).unwrap();
            showToastNotify(response.message, 'success');
            dispatch(setConfirmTypeModal(null))
            dispatch(setUpdateMode({ type: 'actionType', value: 'view'}))
        } catch (error) {
            // console.log("error: ", error)
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, 'error');
        }
    }

    const handleCancelUpdate = (userData: UserInfo) => {
        dispatch(setConfirmTypeModal(null))
        dispatch(setUpdateMode({ type: 'actionType', value: 'view'}))
    };

    const handleCloseViewAccountModal = () => {
        dispatch(clearUserId());
        dispatch(clearTypeModal());
        dispatch(setUpdateMode({ type: 'actionType', value: 'view' }))
        dispatch(setUpdateMode({ type: 'acctId', value: null }))
        dispatch(clearTypeOfStatus())
        dispatch(clearConfirmTypeModal())
    };

    const handleResetPassword = (userId: number) => {
        if (!userId) return;
        if(userId === currentId) {
            showToastNotify("You can't reset your own password.", 'warning');
            return;
        }
        dispatch(setUserId(userId))
        dispatch(setConfirmTypeModal('isResetPassword'))
    };

    const handleDeactivateUser = (userId: number, action: UserStatus) => {
        if (!userId) return;
        if(userId === currentId) {
            showToastNotify("You can't deactivate your own account.", 'warning');
            return;
        }
        dispatch(setUserId(userId))
        dispatch(setConfirmTypeModal('isActivate'))
        dispatch(setTyeOfStatus(action))
    };

    // const handleUpdateProfile = (userId: number) => {   
    const handleUpdateProfile = (data: UserInfo) => {   
        if (!data.user_id || !data) return;
        if(data.user_id === currentId) {
            showToastNotify("You can't update your own profile.", 'warning');
            return;
        }
        
        dispatch(updateField({ field: 'firstname', value: data.firstname }));
        dispatch(updateField({ field: 'middlename', value: data.middlename }));
        dispatch(updateField({ field: 'lastname', value: data.lastname }));
        const suffixName = data.suffix?.trim() || "None";
        const matchedSuffix = suffixOptions.find(
            (option) => option.name.toLowerCase() === suffixName.toLowerCase()
        );
        dispatch(updateField({
            field: 'suffix',
            value: matchedSuffix ?? { id: 100, name: suffixName }
        }));
        dispatch(updateField({ field: 'email', value: data.email }));
        dispatch(updateField({ field: 'agent_code', value: data.agent_code }));
        dispatch(updateField({ field: 'phoneNumber', value: data.phoneNumber?.replace("+63", "") ?? "" }));


        dispatch(updateField({ field: 'position', value: data.position ?? '' }));
        dispatch(updateField({ field: 'role', value: data.role }));
        dispatch(updateField({ field: 'department', value: data.department }));
        dispatch(updateField({ field: 'location', value: data.location }));
        dispatch(updateField({ field: 'reporting_to_id', value: data.reporting_to }));
        dispatch(setUpdateMode({ type: 'actionType', value: 'update'}))
        dispatch(setUpdateMode({ type: 'acctId', value: data.user_id }))
    }

    const handleSelectUser = (userId: number) => {
        dispatch(setUserId(userId));
        dispatch(setTypeModal('viewUser'));
    }

    const handleIsChangePassword = () => {
        dispatch(setAccountSettingMode('updatePassword'))
    }

    const handleCancelChangePassword = () => {
        dispatch(resetUpdatePassword())
        dispatch(setAccountSettingMode('view'))
    }

    const handlePasswordVisibility = (field: keyof PasswordFormState["visible"]) => {
        dispatch(toggleUpdatePasswordVisibility(field));
    }

    const handleUpdateChangePassword = async () => {
        const { password: oldPass, newPassword, confirmPassword } = updatePassword.values;

        if (!oldPass?.trim() || !newPassword?.trim() || !confirmPassword?.trim()) {
            showToastNotify('Please fill in all password fields.', "warning");
            return;
        }

        if (newPassword.length < 6 || confirmPassword.length < 6) {
            showToastNotify("New password must be at least 6 characters.", "warning");
            return;
        }

        if (newPassword !== confirmPassword) {
            showToastNotify('New passwords do not match.', "warning");
            return;
        }
        
        try {
            const response = await updateProfile({password: oldPass, newPassword}).unwrap();
            showToastNotify(`${response.message}`, "success");
            dispatch(resetUpdatePassword())
            dispatch(setAccountSettingMode('view'))
        } catch (error: any) {
            const errorMessage = error?.errors?.[0]?.msg || "Something went wrong";
            showToastNotify(errorMessage, "error");
        }
    }

    const handleCancelUpdateMyAccount = () => {
        dispatch(setAccountSettingMode('view'))
    }

    const handleSaveUpdateMyAccount = async () => {
        const profileData = {
            firstname: selectCreateAccount.firstname ?? "",
            middlename: selectCreateAccount.middlename ?? "",
            lastname: selectCreateAccount.lastname ?? "",
            suffix: selectCreateAccount.suffix?.name === 'None' ? '' :  selectCreateAccount.suffix?.name,
            phoneNumber: selectCreateAccount.phoneNumber ? `+63${selectCreateAccount.phoneNumber}` : ""
        }
        try {
            const response = await updateProfile(profileData).unwrap();
            showToastNotify(`${response.message}`, "success");
            dispatch(setAccountSettingMode('view'))
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showToastNotify(errorMessage, "error");
        }
    }

    return {
        handleCreateAccountClick,
        handleSubmit,
        handleClear,
        handleUpdateUser,
        handleCancelUpdate,
        handleCloseViewAccountModal,
        handleResetPassword,
        handleDeactivateUser,
        handleUpdateProfile,
        isLoadingUpdateProfile,
        handleSelectUser,

        handleIsChangePassword,
        handleCancelChangePassword,
        handlePasswordVisibility,
        handleUpdateChangePassword,

        handleSaveUpdateMyAccount,
        handleCancelUpdateMyAccount,

        isFetchingDepartment,
        isFetchingRoleType,
        isFetchingLocationType,
        isLoadingCreateAccount,
        isErrorUpdateUserAccountInfo,
        isLoadingUpdateUserAccountInfo,
        isFetchingReportingHeadType,

        openDropdown,
        setOpenDropdown,

        suffixOptions,
        roleOptions,
        departmentOptions,
        locationOptions,
        reportingHeadOptions,

        defaultOptionSuffix,
        searchTypeSuffix,
        setSearchTypeSuffix,

        searchTypeRole,
        setSearchTypeRole,

        searchTypeDepartment,
        setSearchTypeDepartment,

        searchTypeLocation,
        setSearchTypeLocation,

        searchReportingHead,
        setSearchReportingHead,

        activeDepartments,
        activeLocations,
        activeRoles,

        updatePosition,
        setUpdatePosition,

        showConfirmModal,
        setShowConfirmModal,

        confirmAction,
        setConfirmAction,
    }
}